import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import { CreateButton } from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Pagination from "../../components/Shared/Pagination/Pagination";
import { ConsultantCollectiveAgreementsBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import useQueryState from "../../hooks/useQueryState";
import { CollectiveAgreement } from "../../models/Consultant";
import { CONSULTANT_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { useCollectiveAgreements } from "../../networking/collective-agreement.hooks";

const ConsultantCollectiveAgreements: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [pageNumber, setPageNumber] = useQueryState<number>("page", {
		initialValue: 1,
		valueFromString: (str) => Number(str),
	});
	const [searchTerm, setSearchTerm] = useQueryState<string>("search");
	const keyword = useDebounce(searchTerm, 500);
	const [order, setOrder] = useQueryState<SORT_ORDER>("order");
	const [sort, setSort] = useQueryState<CONSULTANT_SORTING_COLUMN>("sort");

	const { collectiveAgreementList, isLoading, isFetching } = useCollectiveAgreements({
		page: pageNumber,
		pageSize: PAGE_SIZE,
		keyword,
		sort,
		order,
	});

	const collectiveAgreements = collectiveAgreementList?.collectiveAgreements || [];
	const totalNumberOfCollectiveAgreements = collectiveAgreementList?.totalCount || 0;

	const navigateToAgreement = (id?: string) => {
		if (id) {
			navigate(routes.EDIT_AGREEMENT.replace(":id", id));
		}
	};

	const handleSortingOrder = () => {
		if (!order) {
			setOrder(SORT_ORDER.ASC);
		} else if (order === SORT_ORDER.ASC) {
			setOrder(SORT_ORDER.DESC);
		} else if (order === SORT_ORDER.DESC) {
			setOrder(SORT_ORDER.ASC);
		}
	};

	const handleSortColumn = (sort?: CONSULTANT_SORTING_COLUMN) => {
		handleSortingOrder();
		setSort(sort);
	};

	const formatEmploymentType = (type: CollectiveAgreement["employmentType"]) => {
		const types = {
			fixedTerm: "Visstidsanställning",
			contractor: "Uppdragstagare",
			probation: "Provanställning",
			permanent: "Tillsvidare",
			subcontractor: "Underkonsult",
		};
		return types[type] || type;
	};

	const formatPensionType = (type: CollectiveAgreement["pensionType"]) => {
		const types = {
			itp1: "ITP1",
			itp2: "ITP2",
			notWhiteCollar: "Ej tjänsteman",
		};
		return types[type] || type;
	};

	const formatUnionType = (type: CollectiveAgreement["unionType"]) => {
		const types = {
			noAfa: "Ej AFA (inget kollektivavtal)",
			blueCollar: "Arbetare",
			whiteCollar: "Tjänsteman",
		};
		return types[type] || type;
	};

	return (
		<>
			<Breadcrumbs items={ConsultantCollectiveAgreementsBreadcrumbs} showPoolDropdown={false} />
			<div className="p-4 px-6">
				<Flex justifyContent="space-between">
					<AppInput
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>
					<Link to={routes.CREATE_AGREEMENT}>
						<CreateButton>{t("CREATE_AGREEMENT")}</CreateButton>
					</Link>
				</Flex>

				{isLoading || isFetching ? (
					<LoadingIndicator />
				) : (
					<div>
						{collectiveAgreements.length ? (
							<TableLayout>
								<table>
									<thead>
										<tr>
											<th style={{ minWidth: 250 }}>{t("NAME") || "Namn"}</th>
											<th style={{ width: 200 }}>{"Anställningsform"}</th>
											<th style={{ width: 200 }}>{"Pensionstyp"}</th>
											<th style={{ width: 180 }}>{"Avtalstyp"}</th>
											<th style={{ width: 150 }}>{"Semesterlön %"}</th>
											<th style={{ width: 150 }}>{"GFL"}</th>
										</tr>
									</thead>
									<tbody>
										{collectiveAgreements.map((agreement) => {
											return (
												<tr
													key={agreement.id}
													onClick={() => navigateToAgreement(agreement.id)}
													style={{ cursor: "pointer" }}
												>
													<td>
														<strong>{agreement.name}</strong>
														{agreement.internalDescription && (
															<div style={{ fontSize: "0.875rem", color: "#666" }}>
																{agreement.internalDescription}
															</div>
														)}
													</td>
													<td>{formatEmploymentType(agreement.employmentType)}</td>
													<td>{formatPensionType(agreement.pensionType)}</td>
													<td>{formatUnionType(agreement.unionType)}</td>
													<td>{agreement.vacationSalaryPercent}%</td>
													<td>{agreement.isGflControlled ? t("YES") || "Ja" : t("NO") || "Nej"}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</TableLayout>
						) : (
							<EmptyState margin="2rem 0" label={"Inga kollektivavtal"} />
						)}
						{totalNumberOfCollectiveAgreements > PAGE_SIZE && (
							<Pagination
								currentPage={pageNumber}
								totalCount={totalNumberOfCollectiveAgreements}
								onClickItem={(e) => setPageNumber(e)}
							/>
						)}
					</div>
				)}
			</div>
		</>
	);
};

export default ConsultantCollectiveAgreements;
